import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { DataTable, DataTableColumn } from "../../components/ui/data-table";

interface UserData {
  id: string;
  name: string;
  email: string;
  contact: string;
  affiliation: string;
  createdAt?: any;
  isDelete: boolean;
}

const ManageAccounts: React.FC = () => {
  const [data, setData] = useState<UserData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRows, setSelectedRows] = useState<UserData[]>([]);
  const [filteredAffiliation, setFilteredAffiliation] =
    useState<string>("전체");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const db = getFirestore();
        const usersSnapshot = await getDocs(collection(db, "users"));

        const users: UserData[] = usersSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() } as UserData))
          .filter(
            (user) => user.affiliation !== "총무노트" && user.isDelete === false
          );

        setData(users);
      } catch (error) {
        toast.error("사용자 정보를 불러오는 데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const affiliations = useMemo(() => {
    return Array.from(new Set(data.map((item) => item.affiliation)));
  }, [data]);

  const filteredData = data.filter((user) => {
    const matchesAffiliation =
      filteredAffiliation === "전체" ||
      user.affiliation === filteredAffiliation;
    return matchesAffiliation;
  });

  const columns: DataTableColumn<UserData>[] = [
    {
      title: "No.",
      render: (_: any, __: any, index: number) => index + 1,
      width: 60,
    },
    { title: "이름", dataIndex: "name", sortable: true, sortKey: "name" },
    {
      title: "소속",
      dataIndex: "affiliation",
      sortable: true,
      sortKey: "affiliation",
    },
    { title: "이메일", dataIndex: "email", sortable: true, sortKey: "email" },
    {
      title: "연락처",
      dataIndex: "contact",
      render: (value: string) => {
        const formatted = value?.replace(/^(\d{3})(\d{4})(\d{4})$/, "$1-$2-$3");
        return formatted || "-";
      },
    },
    {
      title: "생성일",
      dataIndex: "createdAt",
      sortable: true,
      sortKey: "createdAt",
      sortFn: (a, b) => {
        const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return ta - tb;
      },
      render: (value: any) =>
        value?.toDate ? dayjs(value.toDate()).format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const handleExport = () => {
    if (selectedRows.length === 0) {
      toast.warning("엑셀로 추출할 데이터를 선택해주세요.");
      return;
    }

    const exportData = selectedRows.map((user, index) => ({
      No: index + 1,
      이름: user.name,
      소속: user.affiliation,
      이메일: user.email,
      연락처: user.contact.replace(/^(\d{3})(\d{4})(\d{4})$/, "$1-$2-$3"),
      생성일: user.createdAt?.toDate
        ? dayjs(user.createdAt.toDate()).format("YYYY-MM-DD HH:mm")
        : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "계정목록");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `계정현황_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold">
            계정 현황{" "}
            <span className="text-sm font-normal text-muted-foreground">
              현재 가입된 고객사 계정 목록입니다.
            </span>
          </h1>
        </div>

        <div className="text-sm text-muted-foreground">
          총 {filteredData.length.toLocaleString()}명
        </div>
      </div>

      <div className="mt-4">
        <DataTable
          rowSelection={{
            onChange: (_keys, rows) => setSelectedRows(rows),
          }}
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          search={{
            keys: ["name", "email", "contact", "affiliation"],
            placeholder: "이름, 소속, 이메일, 연락처 검색",
          }}
          toolbarRight={
            <div className="flex flex-wrap gap-2">
              <Select
                value={filteredAffiliation}
                onValueChange={(value) => setFilteredAffiliation(value)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="소속 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="전체">전체</SelectItem>
                  {affiliations.map((aff) => (
                    <SelectItem key={aff} value={aff}>
                      {aff}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleExport}
                disabled={selectedRows.length === 0}
              >
                엑셀 추출
              </Button>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default ManageAccounts;
